import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { AppHeader } from '../components/AppHeader.js';
import { useGm } from '../hooks/useGm.js';
import '../styles/encounter.css';

export function EncounterIndex(): JSX.Element {
  const { isGm } = useGm();
  const navigate = useNavigate();
  const [list, setList] = useState<Array<{ id: string; name: string; createdAt: number; active: boolean }> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  useEffect(() => {
    if (!isGm) return;
    api.encounters().then((r) => setList(r.encounters)).catch((e: Error) => setErr(e.message));
  }, [isGm]);

  if (!isGm) {
    return (
      <div className="encounter-shell">
        <AppHeader />
        <div className="encounter-body">
          <h1 style={{ fontFamily: 'Cormorant Garamond, serif', color: 'var(--gold-bright)' }}>GM only</h1>
          <p style={{ color: 'var(--text-muted)' }}>
            Ця сторінка лише для GM. Гравці заходять через <code>/room/&lt;encounter-id&gt;/join</code>.
          </p>
        </div>
      </div>
    );
  }

  const createIt = async (): Promise<void> => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const r = await api.encounterCreate(newName.trim());
      navigate(`/encounter/${r.encounter.id}`);
    } catch (e) { setErr((e as Error).message); }
    finally { setCreating(false); }
  };

  return (
    <div className="encounter-shell">
      <AppHeader />
      <div className="encounter-body">
        <h1 style={{ fontFamily: 'Cormorant Garamond, serif', color: 'var(--gold-bright)' }}>Зустрічі</h1>
        <p style={{ color: 'var(--text-muted)' }}>Real-time бойові кімнати. Кожна — окрема зустріч.</p>

        {err && <div style={{ color: 'var(--blood-bright)', marginBottom: 12 }}>⚠ {err}</div>}

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '20px 0' }}>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Назва нової зустрічі"
            style={{
              flex: 1,
              padding: '8px 12px',
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: 4,
              color: 'var(--text)',
              fontFamily: 'Spectral, serif',
              fontSize: 14,
            }}
          />
          <button
            onClick={() => void createIt()}
            disabled={creating || !newName.trim()}
            style={{
              padding: '8px 16px',
              background: 'var(--gold-dim)',
              border: 'none',
              color: 'var(--bg-deep)',
              borderRadius: 4,
              fontFamily: 'Cormorant Garamond, serif',
              cursor: 'pointer',
            }}
          >
            {creating ? 'Створюю…' : '+ Створити'}
          </button>
          <label
            style={{
              padding: '8px 16px',
              background: 'var(--bg-card)',
              border: '1px dashed var(--gold-dim)',
              color: 'var(--gold)',
              borderRadius: 4,
              fontFamily: 'Cormorant Garamond, serif',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            ⤒ Імпорт JSON
            <input
              type="file"
              accept="application/json"
              style={{ display: 'none' }}
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                try {
                  const data = JSON.parse(await f.text());
                  const r = await api.encounterImport(data);
                  navigate(`/encounter/${r.id}`);
                } catch (err) { setErr((err as Error).message); }
              }}
            />
          </label>
        </div>

        {list === null && <p style={{ color: 'var(--text-dim)' }}>завантажую…</p>}
        {list && list.length === 0 && <p style={{ color: 'var(--text-dim)' }}>Зустрічей ще немає.</p>}
        <div className="encounter-list">
          {list?.map((e) => (
            <Link key={e.id} to={`/encounter/${e.id}`} className="player-pick-card">
              <h3>{e.name}</h3>
              <div style={{ fontFamily: 'Spline Sans Mono, monospace', fontSize: 11, color: 'var(--text-dim)' }}>
                id: {e.id}
              </div>
              <div style={{ fontFamily: 'Spline Sans Mono, monospace', fontSize: 11, color: 'var(--text-dim)' }}>
                {new Date(e.createdAt * 1000).toLocaleString('uk-UA')}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
